import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, Plus, Search, Filter, MoreVertical, Eye, Edit2, 
  Trash2, Clock, MapPin, Users, Ticket, DollarSign, Building2,
  CheckCircle2, XCircle, AlertCircle, ArrowRight, Send, Loader2,
  Upload, Copy, Crown
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import ProducerLayout from '../../components/producer/ProducerLayout';
import CoProductionInvitesBanner from '../../components/producer/CoProductionInvitesBanner';
import NotificationBell from '../../components/NotificationBell';

const STATUS_CONFIG = {
  draft: { label: 'Borrador', color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30', icon: AlertCircle },
  pending: { label: 'Pendiente', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
  pending_approval: { label: 'En Revisión', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: Clock },
  published: { label: 'Publicado', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle2 },
  cancelled: { label: 'Cancelado', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
};

const ProducerEventsPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [producer, setProducer] = useState(null);
  const [publishingEventId, setPublishingEventId] = useState(null);
  const [duplicatingEventId, setDuplicatingEventId] = useState(null);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [eventsRes, producerRes, subRes] = await Promise.all([
        api.get('/producers/me/events'),
        api.get('/producers/me'),
        api.get('/producers/me/subscription').catch(() => ({ data: null }))
      ]);
      setEvents(eventsRes.data || []);
      setProducer(producerRes.data);
      setSubscription(subRes.data?.subscription || subRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateEvent = async (eventId, e) => {
    e.stopPropagation();
    const isPremium = subscription?.is_premium;
    if (!isPremium) {
      toast.error('Duplicar eventos es exclusivo de Super Productora. Actualizá tu plan.');
      navigate('/producer/subscription');
      return;
    }
    setDuplicatingEventId(eventId);
    try {
      const res = await api.post(`/producers/me/events/${eventId}/duplicate`);
      toast.success(res.data?.message || 'Evento duplicado');
      await loadData();
      // Navigate to the new event for quick editing
      if (res.data?.event_id) {
        navigate(`/producer/events/${res.data.event_id}/manage`);
      }
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Error al duplicar el evento';
      toast.error(detail);
    } finally {
      setDuplicatingEventId(null);
    }
  };

  const handlePublishEvent = async (eventId, e) => {
    e.stopPropagation();
    setPublishingEventId(eventId);
    try {
      await api.put(`/producers/me/events/${eventId}`, { status: 'published' });
      toast.success('¡Evento publicado exitosamente!');
      loadData();
    } catch (error) {
      console.error('Error publishing:', error);
      toast.error(error.response?.data?.detail || 'Error al publicar el evento');
    } finally {
      setPublishingEventId(null);
    }
  };

  const handleSubmitForApproval = async (eventId, e) => {
    e.stopPropagation();
    setPublishingEventId(eventId);
    try {
      await api.put(`/producers/me/events/${eventId}`, { status: 'pending_approval' });
      toast.success('Evento enviado para aprobación');
      loadData();
    } catch (error) {
      console.error('Error submitting:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar el evento');
    } finally {
      setPublishingEventId(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'America/Argentina/Buenos_Aires'
    });
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = event.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || event.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <ProducerLayout currentPage="events">
      <div className="p-4 lg:p-8" data-testid="producer-events-page">
        <CoProductionInvitesBanner />
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-black text-white">Mis Eventos</h1>
            <p className="text-zinc-400">Gestiona todos tus eventos</p>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell 
              userType="producer" 
              apiEndpoint="/producer/notifications" 
              title="Notificaciones" 
            />
            <Button
              onClick={() => navigate('/producer/events/create')}
              className="bg-gradient-to-r from-violet-600 to-pink-600 text-white"
            >
              <Plus size={18} className="mr-2" />
              Crear Evento
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
            <Input
              placeholder="Buscar eventos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-zinc-900 border-zinc-800"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'draft', 'pending_approval', 'published'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === status 
                    ? 'bg-violet-500 text-black' 
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                }`}
              >
                {status === 'all' ? 'Todos' : STATUS_CONFIG[status]?.label || status}
              </button>
            ))}
          </div>
        </div>

        {/* Events List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredEvents.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <Calendar size={64} className="mx-auto text-zinc-700 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No hay eventos</h3>
            <p className="text-zinc-500 mb-6">Crea tu primer evento para empezar a vender</p>
            <Button
              onClick={() => navigate('/producer/events/create')}
              className="bg-gradient-to-r from-violet-600 to-pink-600 text-white"
            >
              <Plus size={18} className="mr-2" />
              Crear Evento
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {filteredEvents.map((event, index) => {
              const status = STATUS_CONFIG[event.status] || STATUS_CONFIG.draft;
              const StatusIcon = status.icon;
              return (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => navigate(`/producer/events/${event.id}`)}
                  className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden hover:border-violet-500/50 hover:bg-zinc-900/80 transition-all cursor-pointer"
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Image */}
                    <div className="w-full md:w-48 h-32 md:h-auto bg-zinc-800 flex-shrink-0">
                      {event.image_url ? (
                        <img 
                          src={event.image_url} 
                          alt={event.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Calendar size={32} className="text-zinc-600" />
                        </div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 p-4 md:p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-bold text-white">{event.title}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border flex items-center gap-1 ${status.color}`}>
                              <StatusIcon size={12} />
                              {status.label}
                            </span>
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-zinc-400">
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {formatDate(event.date)}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin size={14} />
                              {event.location || 'Sin ubicación'}
                            </span>
                          </div>
                          
                          {/* Stats */}
                          <div className="flex gap-6 mt-4 text-sm">
                            <span className="text-zinc-500">
                              <Ticket size={14} className="inline mr-1" />
                              Vendidas: <span className="text-white">{event.tickets_sold || 0}</span>
                            </span>
                            <span className="text-zinc-500">
                              <DollarSign size={14} className="inline mr-1" />
                              Ingresos: <span className="text-green-400">${event.total_revenue || 0}</span>
                            </span>
                          </div>
                          
                          {/* Rejection reason alert */}
                          {event.rejection_reason && event.status === 'draft' && (
                            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                              <div className="flex items-start gap-2">
                                <XCircle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-red-400 font-medium text-sm">Evento rechazado</p>
                                  <p className="text-red-300/80 text-sm mt-1">{event.rejection_reason}</p>
                                  {event.rejected_at && (
                                    <p className="text-red-400/50 text-xs mt-2">
                                      Rechazado el {new Date(event.rejected_at).toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex flex-col sm:flex-row gap-2 items-end" onClick={(e) => e.stopPropagation()}>
                          {/* Draft event actions */}
                          {event.status === 'draft' && (
                            <>
                              {producer?.is_verified ? (
                                <Button
                                  size="sm"
                                  onClick={(e) => handlePublishEvent(event.id, e)}
                                  disabled={publishingEventId === event.id}
                                  className="bg-gradient-to-r from-violet-600 to-pink-600 text-white"
                                  data-testid={`publish-btn-${event.id}`}
                                >
                                  {publishingEventId === event.id ? (
                                    <Loader2 size={14} className="animate-spin mr-1" />
                                  ) : (
                                    <Upload size={14} className="mr-1" />
                                  )}
                                  Publicar
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  onClick={(e) => handleSubmitForApproval(event.id, e)}
                                  disabled={publishingEventId === event.id}
                                  className="bg-purple-600 hover:bg-purple-700 text-white"
                                  data-testid={`submit-approval-btn-${event.id}`}
                                >
                                  {publishingEventId === event.id ? (
                                    <Loader2 size={14} className="animate-spin mr-1" />
                                  ) : (
                                    <Send size={14} className="mr-1" />
                                  )}
                                  Enviar Solicitud
                                </Button>
                              )}
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDuplicateEvent(event.id, e)}
                            disabled={duplicatingEventId === event.id}
                            className={`relative ${subscription?.is_premium ? 'text-zinc-400 hover:text-violet-500' : 'text-zinc-500 hover:text-amber-400'}`}
                            title={subscription?.is_premium ? 'Duplicar evento' : 'Duplicar evento (Super Productora)'}
                            data-testid={`duplicate-btn-${event.id}`}
                          >
                            {duplicatingEventId === event.id ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <Copy size={16} />
                            )}
                            {!subscription?.is_premium && (
                              <Crown size={10} className="absolute -top-0.5 -right-0.5 text-amber-400" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(`/evento/${event.id}`, '_blank')}
                            className="text-zinc-400 hover:text-violet-500"
                          >
                            <Eye size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </ProducerLayout>
  );
};

export default ProducerEventsPage;
